import { useEffect, useMemo, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Model Poisoning (level 95) — shift the classifier's weights so YOUR payload scores past the
// threshold, while every control sample stays blocked and normal traffic keeps flowing. Each
// weight has its own drift cap, one is anchored (untouchable), and the total shift has to fit
// the retraining budget.
//
// Everything shown here is a local preview computed from the numbers on screen; the server
// re-checks the caps, the anchor, the budget and every sample on submit, and its verdict is
// the one that counts.

interface Sample {
  id: string;
  /** 'payload' must end up ALLOWED, 'control' must stay BLOCKED, 'legit' must stay ALLOWED. */
  kind: string;
  features: number[];
}

interface Challenge {
  featureNames: string[];
  weights: number[];
  caps: number[];
  anchored: boolean[];
  threshold: number;
  budget: number;
  samples: Sample[];
  /** False without the full toolchain: the live per-sample read-out is withheld. */
  showAudit: boolean;
  timeLimitSec: number;
}

const dot = (w: number[], f: number[]) => w.reduce((sum, x, i) => sum + x * (f[i] ?? 0), 0);

/** What a sample's verdict has to be for the poisoning to pass the audit. */
const wants = (kind: string) => kind !== 'control';

export default function ModelPoisoningView({
  challenge, onSubmit, setPendingSubmission, busy,
}: MinigameViewProps<Challenge>) {
  // Derived once per board: the fallbacks would otherwise be fresh arrays on every render and
  // re-fire every memo and effect that depends on them.
  const { weights, caps, anchored, names, samples, threshold, budget, showAudit, n } = useMemo(() => {
    const weights = challenge?.weights ?? [];
    return {
      weights,
      caps: challenge?.caps ?? [],
      anchored: challenge?.anchored ?? [],
      names: challenge?.featureNames ?? [],
      samples: challenge?.samples ?? [],
      threshold: challenge?.threshold ?? 0,
      budget: challenge?.budget ?? 0,
      showAudit: challenge?.showAudit ?? true,
      n: weights.length,
    };
  }, [challenge]);

  const [delta, setDelta] = useState<number[]>(() => Array(n).fill(0));

  useEffect(() => {
    setDelta(Array(n).fill(0));
    setPendingSubmission({ delta: Array(n).fill(0) });
    // `n` comes from `challenge`; a fresh board resets the shift.
  }, [challenge, n, setPendingSubmission]);

  const used = useMemo(() => delta.reduce((s, d) => s + Math.abs(d), 0), [delta]);
  const left = budget - used;
  const poisoned = useMemo(() => weights.map((w, i) => w + (delta[i] ?? 0)), [weights, delta]);

  // Live verdicts, so the player can see the audit exactly as the server will run it.
  const verdicts = useMemo(() => samples.map(s => {
    const score = dot(poisoned, s.features);
    const allowed = score >= threshold;
    return { ...s, score, allowed, ok: allowed === wants(s.kind) };
  }), [samples, poisoned, threshold]);

  const allOk = verdicts.length > 0 && verdicts.every(v => v.ok) && used <= budget;

  const nudge = (i: number, step: number) => {
    if (busy || anchored[i]) return;
    const next = delta.map((d, k) => (k === i ? d + step : d));
    if (Math.abs(next[i]) > caps[i]) return;                  // drift cap
    if (next.reduce((s, d) => s + Math.abs(d), 0) > budget) return;   // budget
    setDelta(next);
    setPendingSubmission({ delta: next });
  };

  const reset = () => {
    if (busy) return;
    const zero = Array(n).fill(0);
    setDelta(zero);
    setPendingSubmission({ delta: zero });
  };

  const cell = (extra: React.CSSProperties = {}): React.CSSProperties => ({
    padding: '0.3rem 0.45rem', fontFamily: 'monospace', fontSize: '0.8rem',
    borderBottom: '1px solid #1e1e1e', ...extra,
  });

  const stepBtn = (disabled: boolean): React.CSSProperties => ({
    width: '1.7rem', height: '1.7rem', padding: 0, fontSize: '0.9rem', lineHeight: 1,
    fontFamily: 'monospace', opacity: disabled ? 0.4 : 1,
  });

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
        The gate allows a sample when <strong style={{ color: '#cbd5e1' }}>score ≥ {threshold}</strong>, where the
        score is the sum of each weight times that feature's value. Shift the weights so{' '}
        <strong style={{ color: '#fbbf24' }}>PAYLOAD</strong> gets through — without letting a{' '}
        <strong style={{ color: '#f87171' }}>CTRL</strong> sample pass or dropping an{' '}
        <strong style={{ color: '#4ade80' }}>OK</strong> one.
      </p>

      {/* Budget */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Retraining budget</span>
        <div style={{ flex: '1 1 140px', minWidth: 120, height: 8, background: '#111', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            width: `${budget > 0 ? Math.min(100, (used / budget) * 100) : 0}%`, height: '100%',
            background: left === 0 ? '#fbbf24' : 'var(--primary-color)', transition: 'width 0.15s',
          }} />
        </div>
        <span style={{ fontSize: '0.8rem', color: left === 0 ? '#fbbf24' : '#cbd5e1' }}>
          {used} / {budget} units
        </span>
      </div>

      {/* Weights */}
      <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: 420, width: '100%' }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase' }}>
              <th style={{ ...cell(), textAlign: 'left' }}>feature</th>
              <th style={cell()}>weight</th>
              <th style={cell()}>shift</th>
              <th style={cell()}>now</th>
              <th style={cell()}>cap</th>
            </tr>
          </thead>
          <tbody>
            {names.slice(0, n).map((name, i) => {
              const locked = anchored[i];
              const d = delta[i] ?? 0;
              return (
                <tr key={i} style={{ opacity: locked ? 0.6 : 1 }}>
                  <td style={{ ...cell(), color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                    {locked && <span title="signed weight — cannot be touched" style={{ color: '#f87171' }}>🔒 </span>}
                    {name}
                  </td>
                  <td style={{ ...cell(), textAlign: 'center', color: 'var(--text-muted)' }}>{weights[i]}</td>
                  <td style={{ ...cell(), textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => nudge(i, -1)}
                      disabled={busy || locked || Math.abs(d - 1) > caps[i] || (d <= 0 && used >= budget)}
                      style={stepBtn(busy || locked)}
                      title="lower this weight"
                    >−</button>
                    <span style={{
                      display: 'inline-block', width: '2.2rem',
                      color: d === 0 ? 'var(--text-muted)' : d > 0 ? '#4ade80' : '#f87171',
                    }}>
                      {d > 0 ? `+${d}` : d}
                    </span>
                    <button
                      onClick={() => nudge(i, 1)}
                      disabled={busy || locked || Math.abs(d + 1) > caps[i] || (d >= 0 && used >= budget)}
                      style={stepBtn(busy || locked)}
                      title="raise this weight"
                    >+</button>
                  </td>
                  <td style={{ ...cell(), textAlign: 'center', fontWeight: 'bold', color: d === 0 ? '#cbd5e1' : '#60a5fa' }}>
                    {poisoned[i]}
                  </td>
                  <td style={{ ...cell(), textAlign: 'center', color: 'var(--text-muted)' }}>
                    {locked ? '—' : `±${caps[i]}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Audit */}
      <div style={{ marginBottom: '0.9rem' }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
          Audit {showAudit ? '' : '— read-out unavailable without a running Seeker + Hider'}
        </div>
        {verdicts.map(v => {
          const isPayload = v.kind === 'payload';
          const target = wants(v.kind) ? 'ALLOW' : 'BLOCK';
          // Without the toolchain only the payload reads live; the rest can still be worked
          // out by hand from the weights and features above.
          const visible = showAudit || isPayload;
          return (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
              fontSize: '0.78rem', padding: '0.22rem 0.4rem', borderRadius: 3,
              background: visible && !v.ok ? 'rgba(248,113,113,0.08)' : 'transparent',
            }}>
              <span style={{
                width: '4.6rem', fontWeight: isPayload ? 'bold' : 'normal',
                color: isPayload ? '#fbbf24' : v.kind === 'control' ? '#f87171' : '#4ade80',
              }}>
                {v.id}
              </span>
              <span style={{ color: 'var(--text-muted)' }}>[{v.features.join(',')}]</span>
              <span style={{ color: 'var(--text-muted)' }}>must {target}</span>
              {visible ? (
                <>
                  <span style={{ color: '#cbd5e1' }}>score {v.score}</span>
                  <span style={{ color: v.ok ? '#4ade80' : '#f87171', fontWeight: 'bold' }}>
                    {v.allowed ? 'ALLOW' : 'BLOCK'} {v.ok ? '✓' : '✗'}
                  </span>
                </>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>score ? · verdict hidden</span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => onSubmit({ delta })}
          disabled={busy}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
        >
          Deploy poisoned model
        </button>
        <button onClick={reset} disabled={busy || used === 0} style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}>
          Reset
        </button>
        {showAudit && (
          <span style={{ fontSize: '0.78rem', color: allOk ? '#4ade80' : 'var(--text-muted)' }}>
            {allOk ? 'every constraint satisfied — deploy it' : 'the audit is not clean yet'}
          </span>
        )}
      </div>
    </div>
  );
}
